import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const GraficaRiesgo = ({ datos }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!datos) return;

        const chartInstance = echarts.init(chartRef.current);

        // Función para formatear números sin decimales y con comas para miles
        const formatNumber = (value) => {
            const rounded = Math.round(value);
            return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        };

        // Función para convertir string con comas a número
        const parseNumber = (value) => {
            if (typeof value === 'string') {
                return parseFloat(value.replace(/,/g, ''));
            }
            return value;
        };

        // Función para generar color con opacidad basada en el valor relativo
        const generateGradientColor = (baseColor, intensity) => {
            // intensity va de 0 a 1, donde 1 es el valor más alto
            const minOpacity = 0.3;
            const maxOpacity = 1.0;
            const opacity = minOpacity + (intensity * (maxOpacity - minOpacity));

            // Convertir hex a rgb para aplicar opacidad
            const hexToRgb = (hex) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16)
                } : null;
            };

            const rgb = hexToRgb(baseColor);
            if (rgb) {
                return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
            }
            return baseColor;
        };

        // Extraer datos principales
        const riesgos = datos.grafico_riesgos || {};
        const confidencialidad = riesgos.Confidencialidad || 0;
        const integridad = riesgos.Integridad || 0;
        const disponibilidad = riesgos.Disponibilidad || 0;

        // Suma total
        const total = confidencialidad + integridad + disponibilidad;

        // Datos principales (anillo interior)
        const datosPrincipales = [
            { value: confidencialidad, name: 'Confidencialidad', itemStyle: { color: '#47D7AC' } },
            { value: integridad, name: 'Integridad', itemStyle: { color: '#F6863C' } },
            { value: disponibilidad, name: 'Disponibilidad', itemStyle: { color: '#7A1FFF' } }
        ];

        // Arrays por categoría
        let subseccionesConf = [];
        let subseccionesInt = [];
        let subseccionesDisp = [];

        let sumaConfidencialidad = 0;
        let sumaIntegridad = 0;
        let sumaDisponibilidad = 0;

        // Colores base para cada categoría
        const coloresBase = {
            'Confidencialidad': '#47D7AC',
            'Integridad': '#F6863C',
            'Disponibilidad': '#7A1FFF'
        };

        // Confidencialidad (máx 5)
        if (datos.tabla_c && datos.tabla_c.length > 0) {
            datos.tabla_c.slice(0, 5).forEach((item, index) => {
                const valor = parseNumber(item.exposicioninherentep95);
                if (valor > 0) {
                    sumaConfidencialidad += valor;
                    subseccionesConf.push({
                        value: valor,
                        name: `${item.nombre}`,
                        categoria: 'Confidencialidad'
                    });
                }
            });
        }

        // Integridad (máx 5)
        if (datos.tabla_i && datos.tabla_i.length > 0) {
            datos.tabla_i.slice(0, 5).forEach((item, index) => {
                const valor = parseNumber(item.exposicioninherentep95);
                if (valor > 0) {
                    sumaIntegridad += valor;
                    subseccionesInt.push({
                        value: valor,
                        name: `${item.nombre}`,
                        categoria: 'Integridad'
                    });
                }
            });
        }

        // Disponibilidad (máx 5)
        if (datos.tabla_d && datos.tabla_d.length > 0) {
            datos.tabla_d.slice(0, 5).forEach((item, index) => {
                const valor = parseNumber(item.exposicioninherentep95);
                if (valor > 0) {
                    sumaDisponibilidad += valor;
                    subseccionesDisp.push({
                        value: valor,
                        name: `${item.nombre}`,
                        categoria: 'Disponibilidad'
                    });
                }
            });
        }

        // Otros por categoría
        const otrosConf = confidencialidad - sumaConfidencialidad;
        if (otrosConf > 0) {
            subseccionesConf.push({
                value: otrosConf,
                name: 'Otros',
                categoria: 'Confidencialidad'
            });
        }

        const otrosInt = integridad - sumaIntegridad;
        if (otrosInt > 0) {
            subseccionesInt.push({
                value: otrosInt,
                name: 'Otros',
                categoria: 'Integridad'
            });
        }

        const otrosDisp = disponibilidad - sumaDisponibilidad;
        if (otrosDisp > 0) {
            subseccionesDisp.push({
                value: otrosDisp,
                name: 'Otros',
                categoria: 'Disponibilidad'
            });
        }

        // Función para aplicar degradados a una categoría
        const aplicarDegradados = (subsecciones, categoria) => {
            if (subsecciones.length === 0) return [];

            // Encontrar el valor máximo y mínimo en esta categoría
            const valores = subsecciones.map(item => item.value);
            const valorMax = Math.max(...valores);
            const valorMin = Math.min(...valores);
            const rango = valorMax - valorMin;

            return subsecciones.map(item => {
                // Calcular intensidad (0 a 1)
                let intensidad = 1; // Por defecto máxima intensidad
                if (rango > 0) {
                    intensidad = (item.value - valorMin) / rango;
                }

                return {
                    ...item,
                    itemStyle: {
                        color: generateGradientColor(coloresBase[categoria], intensidad)
                    }
                };
            });
        };

        // Aplicar degradados a cada categoría
        const subseccionesConfConDegradado = aplicarDegradados(subseccionesConf, 'Confidencialidad');
        const subseccionesIntConDegradado = aplicarDegradados(subseccionesInt, 'Integridad');
        const subseccionesDispConDegradado = aplicarDegradados(subseccionesDisp, 'Disponibilidad');

        // Unir en orden para respetar posición
        const subsecciones = [
            ...subseccionesConfConDegradado,
            ...subseccionesIntConDegradado,
            ...subseccionesDispConDegradado
        ];

        const option = {
            title: {
                text: `Exposición Total: ${formatNumber(total)}`,
                left: 'center',
                top: '5%',
                textStyle: {
                    fontSize: 24,
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    fontFamily: 'Arial, sans-serif'
                }
            },
            legend: {
                left: '2%',
                top: 'middle',
                orient: 'vertical',
                data: datosPrincipales.map(item => item.name),
                textStyle: {
                    fontSize: 13,
                    color: '#34495e'
                },
                itemGap: 20,
                icon: 'circle',
                itemWidth: 14,
                itemHeight: 14
            },
            toolbox: {
                show: true,
                right: '3%',
                top: '20%',
                orient: 'vertical',
                feature: {
                    restore: { show: true, title: 'Restaurar' },
                    saveAsImage: { show: true, title: 'Guardar Imagen', name: 'grafico_riesgo' }
                },
                iconStyle: { borderColor: '#95a5a6' },
                emphasis: { iconStyle: { borderColor: '#3498db' } }
            },
            backgroundColor: '#fafafa',
            tooltip: {
                trigger: 'item',
                formatter: function (params) {
                    return `<strong>${params.name}</strong><br/>
                            Valor: ${formatNumber(params.value)}<br/>
                            Porcentaje: ${params.percent}%`;
                }
            },
            series: [
                {
                    name: 'Categorías Principales',
                    type: 'pie',
                    radius: [60, 140],
                    center: ['60%', '55%'],
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 3,
                        shadowBlur: 5,
                        shadowColor: 'rgba(0, 0, 0, 0.1)',
                        shadowOffsetX: 2,
                        shadowOffsetY: 2
                    },
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (params) => `${params.name}\n${formatNumber(params.value)}`,
                        fontSize: 14,
                        fontWeight: 'bold',
                        color: '#000',
                        textShadowColor: 'rgba(255, 255, 255, 0.8)',
                        textShadowBlur: 2
                    },
                    labelLine: { show: false },
                    data: datosPrincipales
                },
                {
                    name: 'Detalle por Activo',
                    type: 'pie',
                    radius: [155, 200],
                    center: ['60%', '55%'],
                    itemStyle: {
                        borderRadius: 6,
                        borderColor: '#fff',
                        borderWidth: 2,
                        shadowBlur: 3,
                        shadowColor: 'rgba(0, 0, 0, 0.1)'
                    },
                    label: {
                        show: true,
                        position: 'outside',
                        formatter: (params) => `${params.name}\n${formatNumber(params.value)}`,
                        fontSize: 11,
                        color: '#2c3e50',
                        fontWeight: '500',
                        lineHeight: 14
                    },
                    labelLine: {
                        show: true,
                        length: 15,
                        length2: 8,
                        smooth: true,
                        lineStyle: { color: '#bdc3c7', width: 1 }
                    },
                    data: subsecciones
                }
            ],
            animationType: 'scale',
            animationEasing: 'elasticOut',
            animationDelay: (idx) => Math.random() * 200
        };

        chartInstance.setOption(option);

        // Cleanup
        return () => {
            chartInstance.dispose();
        };
    }, [datos]);

    return (
        <div
            style={{
                width: '100%',
                height: 650,
                backgroundColor: '#fafafa',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                padding: '10px'
            }}
            ref={chartRef}
        />
    );
};

export default GraficaRiesgo;